import { Box, Flex, TextV2 as Text, InputV2 as Input } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { isEqual } from 'lodash-es';
import { FiltersPanelProps } from './FiltersPanel.types';
import { GuestRating } from '../../hooks/useListingFilters';
import { StarRating } from '@shared/components';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { debounce } from 'lodash-es';
import { checkboxOverrides, StyledCheckboxV2, FiltersPanelSkeleton, StyledSlider, PriceFilterSkeleton } from './components';

const STAR_RATING_VALUES = [5, 4, 3, 2, 1];

function updateArrayValues<T>(source: T[], newValue: T) {
  return source.includes(newValue) ? source.filter(r => r !== newValue) : [...source, newValue];
}

const Container: React.FC<{}> = ({ children }) => {
  return (
    <Flex flexDirection="column" padding={6} width={292} maxWidth={pxToRem(292)} height="100%" overflow="auto" rowGap={6}>
      {children}
    </Flex>
  );
};

const FiltersPanel = ({ filters, onFiltersChange, supportedFacilities, facilityCounts, isLoadingPrices, pricingError }: FiltersPanelProps) => {
  const { t } = useTranslation('accommodationsConsole');
  const filterPanelTrans = t('filterPanel');
  const [priceRange, setPriceRange] = useState<[number, number]>(filters.priceRange || [0, 1000]);
  const [debouncedPriceRange, setDebouncedPriceRange] = useState<[number, number]>(filters.priceRange || [0, 1000]);

  useEffect(() => {
    if (!isEqual(priceRange, filters.priceRange)) {
      setPriceRange(filters.priceRange);
      setDebouncedPriceRange(filters.priceRange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.priceRange]);

  const guestRatingsOptions = useMemo(
    () => [
      { rating: 9, text: filterPanelTrans.guestRating.wonderful() },
      { rating: 8, text: filterPanelTrans.guestRating.veryGood() },
      { rating: 7, text: filterPanelTrans.guestRating.good() },
      { rating: 6, text: filterPanelTrans.guestRating.pleasant() }
    ],
    [filterPanelTrans]
  );

  const handleFacilityToggle = useEventCallback((facility: string) => {
    onFiltersChange({ ...filters, facilities: updateArrayValues(filters.facilities || [], facility) });
  });

  const handleStarRatingToggle = useEventCallback((rating: number): void => {
    onFiltersChange({ ...filters, starRating: updateArrayValues(filters.starRating || [], rating) });
  });

  const handleUserRatingToggle = useEventCallback((rating: GuestRating): void => {
    onFiltersChange({ ...filters, userRating: updateArrayValues(filters.userRating || [], rating) });
  });

  const handlePriceRangeChange = useEventCallback((value: number | readonly number[]) => {
    const newRange: [number, number] = Array.isArray(value) ? [value[0], value[1]] : [value, value];
    setPriceRange(newRange);
  });

  const renderPriceFilter = useCallback(() => {
    if (pricingError) {
      return null;
    }

    if (isLoadingPrices) {
      return <PriceFilterSkeleton />;
    }
    return (
      <Box>
        <Text typographyVariant="hed2" marginBottom={2}>
          {filterPanelTrans.priceRange.title()}
        </Text>
        <Text typographyVariant="body1" color="mono10" marginBottom={pxToRem(24)}>
          {filterPanelTrans.priceRange.description()}
        </Text>
        <Box marginBottom={3}>
          <StyledSlider value={priceRange} onChange={handlePriceRangeChange} min={0} max={1000} className="slider" trackClassName="track" thumbClassName="thumb" />
        </Box>
        <Flex gap={3} alignItems="center">
          <Box>
            <Text typographyVariant="label3" marginBottom={3}>
              {filterPanelTrans.priceRange.min()}
            </Text>
            <Input
              type="number"
              value={priceRange[0]}
              onChange={e => handlePriceRangeChange([parseInt(e.target.value, 10) || 0, priceRange[1]])}
              placeholder="Min"
              startElement={<Text typographyVariant="body2">$</Text>}
              style={{ paddingLeft: pxToRem(32) }}
              min={0}
            />
          </Box>
          <Text marginTop={pxToRem(24)}>-</Text>
          <Box>
            <Text typographyVariant="label3" marginBottom={3}>
              {filterPanelTrans.priceRange.max()}
            </Text>
            <Input
              type="number"
              value={priceRange[1]}
              onChange={e => handlePriceRangeChange([priceRange[0], parseInt(e.target.value, 10) || 3500])}
              placeholder="Max"
              startElement={<Text typographyVariant="body2">$</Text>}
              style={{ paddingLeft: pxToRem(32) }}
              min={0}
            />
          </Box>
        </Flex>
      </Box>
    );
  }, [filterPanelTrans, isLoadingPrices, pricingError, priceRange, handlePriceRangeChange]);

  useEffect(() => {
    const debouncedUpdate = debounce((value: [number, number]) => {
      setDebouncedPriceRange(value);
    }, 1000);

    debouncedUpdate(priceRange);
    return () => debouncedUpdate.cancel();
  }, [priceRange]);

  useEffect(() => {
    onFiltersChange({
      ...filters,
      priceRange: debouncedPriceRange
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPriceRange]);

  return (
    <Container>
      <Text typographyVariant="hed4">{filterPanelTrans.title()}</Text>

      <Box>
        <Text typographyVariant="hed2" marginBottom={pxToRem(20)}>
          {filterPanelTrans.guestRating.title()}
        </Text>
        <Flex flexDirection="column" gap={5}>
          {guestRatingsOptions.map(({ rating, text }) => (
            <StyledCheckboxV2
              key={rating}
              isChecked={filters.userRating?.includes(rating as GuestRating) || false}
              onChange={() => handleUserRatingToggle(rating as GuestRating)}
              id={`guest-rating-${rating}`}
              overrides={checkboxOverrides}
            >
              <Text typographyVariant="body2" color="mono14">
                {text}
              </Text>
            </StyledCheckboxV2>
          ))}
        </Flex>
      </Box>

      {renderPriceFilter()}

      <Box>
        <Text typographyVariant="hed2" marginBottom={2}>
          {filterPanelTrans.starRating.title()}
        </Text>
        <Text typographyVariant="body1" color="mono10" marginBottom={pxToRem(24)}>
          {filterPanelTrans.starRating.description()}
        </Text>
        <Flex flexDirection="column" gap={5}>
          {STAR_RATING_VALUES.map(rating => (
            <StyledCheckboxV2
              key={rating}
              isChecked={filters.starRating?.includes(rating) || false}
              onChange={() => handleStarRatingToggle(rating)}
              id={`star-rating-${rating}`}
              overrides={checkboxOverrides}
            >
              <Flex alignItems="center" gap={2}>
                <Text typographyVariant="body2" color="mono14">
                  {filterPanelTrans.starRating.starWithCount({ star: rating })}
                </Text>
                <StarRating rating={rating} hideNonFilledStars id={`star-rating-${rating}`} />
              </Flex>
            </StyledCheckboxV2>
          ))}
        </Flex>
      </Box>

      <Box>
        <Text typographyVariant="hed2" marginBottom={pxToRem(20)}>
          {filterPanelTrans.facilities.title()}
        </Text>
        <Flex flexDirection="column" gap={5}>
          {supportedFacilities.map(facility => (
            <StyledCheckboxV2
              key={facility.label}
              isChecked={filters.facilities?.includes(facility.label) || false}
              onChange={() => handleFacilityToggle(facility.label)}
              id={`facility-${facility.label}`}
              overrides={checkboxOverrides}
            >
              <Flex alignItems="center" gap={2}>
                <Text typographyVariant="body2" color="mono14">
                  {facility.label}
                </Text>
                <Text typographyVariant="body2" color="mono10">
                  ({facilityCounts[facility.label]})
                </Text>
              </Flex>
            </StyledCheckboxV2>
          ))}
        </Flex>
      </Box>
    </Container>
  );
};

FiltersPanel.Skeleton = () => {
  return (
    <Container>
      <FiltersPanelSkeleton />
    </Container>
  );
};

export { FiltersPanel };
